import { RoutineOccasionStatus } from "./__generated__/WorkspaceRoutineOccasionStoresList_routineWorkspaceOccasion.graphql";
import { WorkspaceRoutineOccasionOrderByKey } from "./WorkspaceRoutineOccasionListHeader";

type SortableOccasion = {
  store: { name: string };
  occasion: {
    id: string;
    registeredIssueCount: number;
    issueCount: number;
    occasionStatus: RoutineOccasionStatus;
  };
};

function sortTotalIssueCount(
  { occasion: { registeredIssueCount: a } }: SortableOccasion,
  { occasion: { registeredIssueCount: b } }: SortableOccasion
) {
  return a - b;
}

function sortActiveIssueCount(
  { occasion: { issueCount: a } }: SortableOccasion,
  { occasion: { issueCount: b } }: SortableOccasion
) {
  return a - b;
}

function getRoutineOccasionStatusValue(status: RoutineOccasionStatus) {
  switch (status) {
    case "FUTURE":
      return 0;
    case "COMPLETED":
      return 1;
    case "NO_TASKS_COMPLETED":
      return 1;
    case "REQUIRED_COMPLETED":
      return 2;
    case "IN_PROGRESS":
      return 3;
    case "NOT_COMPLETED_STARTED":
    case "NOT_COMPLETED":
      return 4;
    case "IN_PROGRESS_EXPIRED":
      return 4;
  }
}

function sortStatus(
  { occasion: { occasionStatus: a } }: SortableOccasion,
  { occasion: { occasionStatus: b } }: SortableOccasion
) {
  return getRoutineOccasionStatusValue(a) - getRoutineOccasionStatusValue(b);
}

function sortName(
  { store: { name: a } }: SortableOccasion,
  { store: { name: b } }: SortableOccasion
) {
  return a.localeCompare(b);
}

function sortId(
  { occasion: { id: a } }: SortableOccasion,
  { occasion: { id: b } }: SortableOccasion
) {
  return a.localeCompare(b);
}

const sortWithFallback =
  (fun: (a: SortableOccasion, b: SortableOccasion) => number) =>
  (a: SortableOccasion, b: SortableOccasion) => {
    const value = fun(a, b);
    if (value !== 0) {
      return value;
    }
    const nameValue = sortName(a, b);
    if (nameValue !== 0) {
      return nameValue;
    }
    return sortId(a, b);
  };

function getSortFunction(orderBy: WorkspaceRoutineOccasionOrderByKey | null) {
  switch (orderBy) {
    case "store":
      return sortName;
    case "totalIssueCount":
      return sortTotalIssueCount;
    case "activeIssueCount":
      return sortActiveIssueCount;
    case "status":
      return sortStatus;
    case null:
      return sortName;
  }
}

export function workspaceRoutineOccasionSort<T extends SortableOccasion[]>(
  occasions: T,
  orderBy: WorkspaceRoutineOccasionOrderByKey | null,
  order: "ASC" | "DESC"
) {
  const sortFunction = sortWithFallback(getSortFunction(orderBy));
  if (order === "DESC") {
    return occasions.sort((a, b) => -sortFunction(a, b));
  }
  return occasions.sort(sortFunction);
}
